import { useState, useRef } from 'react'
import { Outlet, NavLink, useLocation } from 'react-router-dom'
import { clsx } from 'clsx'
import { useAdminAuth } from '@/auth/useAdminAuth'
import { ThemeSwitcher } from '@/components/ThemeSwitcher'
import { AccountDrawer } from '@/components/AccountDrawer'
import { ToastHost } from '@/components/ToastHost'

interface NavItem {
  label: string
  icon: string
  path: string
  phase?: string
}

interface NavSection {
  title: string
  items: NavItem[]
}

const navigation: NavSection[] = [
  {
    title: '',
    items: [{ label: 'Dashboard', icon: 'pi-th-large', path: '' }],
  },
  {
    title: 'PLATFORM',
    items: [
      { label: 'Tenants', icon: 'pi-building', path: 'tenants' },
      { label: 'Tenants Audit Log', icon: 'pi-history', path: 'tenant-audit-log' },
      { label: 'Tenants Login Audit', icon: 'pi-sign-in', path: 'login-audit' },
      { label: 'Access Control', icon: 'pi-lock', path: 'access-control' },
      { label: 'Authentication', icon: 'pi-shield', path: 'authentication' },
      { label: 'Audit Log', icon: 'pi-list', path: 'audit-log' },
    ],
  },
  {
    // Ratified 2026-09-29 (campaign workflow-orchestration): one workflow system.
    // The Feb-2026 "Workflows" prototype is retired (route kept, nav entry removed —
    // its Run button only ever inserted a row). Workflow Templates and the AI
    // Training Log moved up from the dissolved USER WORKFLOW section.
    title: 'DENTAL AI-MODELS ORCHESTRATION',
    items: [
      { label: 'AI Models', icon: 'pi-box', path: 'ai-models' },
      { label: 'Workflow Templates', icon: 'pi-sitemap', path: 'workflow-templates' },
      { label: 'Step Catalogue', icon: 'pi-th-large', path: 'step-catalogue' },
      { label: 'Workers', icon: 'pi-server', path: 'workers' },
      { label: 'Dispatch Policy', icon: 'pi-directions-alt', path: 'dispatch-policy' },
      { label: 'Job Queue', icon: 'pi-list', path: 'jobs' },
      { label: 'Pipeline Watch', icon: 'pi-eye', path: 'pipeline-watch' },
      { label: 'AI Training Log', icon: 'pi-list-check', path: 'ai-training-log' },
      { label: 'Model Handoff Logs', icon: 'pi-arrow-right-arrow-left', path: 'model-handoff-logs' },
    ],
  },
  {
    title: 'TOOLS',
    items: [
      { label: 'Drydock Analysis', icon: 'pi-search', path: 'drydock' },
      { label: 'Marketplace', icon: 'pi-shopping-bag', path: 'marketplace' },
      { label: 'Feature Flags', icon: 'pi-flag', path: 'feature-flags' },
    ],
  },
  {
    title: 'BURST COMPUTE',
    items: [
      { label: 'Autopilot', icon: 'pi-bolt', path: 'burst/autopilot' },
      { label: 'Jobs', icon: 'pi-list', path: 'burst/jobs' },
      { label: 'Clusters', icon: 'pi-server', path: 'burst/clusters' },
      { label: 'Cost', icon: 'pi-chart-line', path: 'burst/cost' },
      { label: 'Cost by Clinic', icon: 'pi-building', path: 'burst/cost-by-clinic' },
      { label: 'Cost Caps', icon: 'pi-dollar', path: 'burst/caps' },
      { label: 'Policies', icon: 'pi-sliders-h', path: 'burst/policies' },
      { label: 'Audit', icon: 'pi-history', path: 'burst/audit' },
      { label: 'DLQ', icon: 'pi-exclamation-triangle', path: 'burst/dlq' },
      { label: 'BLOCKER Proofs', icon: 'pi-verified', path: 'burst/proofs' },
    ],
  },
  {
    title: 'AI PROVIDERS & PROMPTS',
    items: [
      { label: 'AI Settings', icon: 'pi-sliders-h', path: 'ai-settings' },
      { label: 'Custom Agents', icon: 'pi-bolt', path: 'custom-agents' },
      { label: 'Prompt Templates', icon: 'pi-pencil', path: 'prompt-templates' },
    ],
  },
  {
    title: 'KB — KNOWLEDGE',
    items: [
      { label: 'Knowledge Base', icon: 'pi-book', path: 'knowledge-base' },
    ],
  },
  {
    title: 'COMMUNICATIONS',
    items: [
      { label: 'Messaging', icon: 'pi-envelope', path: 'messaging' },
      { label: 'Chat', icon: 'pi-comments', path: 'chat' },
      { label: 'OTP Settings', icon: 'pi-mobile', path: 'otp-settings' },
    ],
  },
  {
    title: 'SYSTEM',
    items: [
      { label: 'Settings', icon: 'pi-cog', path: 'settings' },
      { label: 'Notifications', icon: 'pi-bell', path: 'notifications' },
      { label: 'Network', icon: 'pi-globe', path: 'network' },
      { label: 'Database', icon: 'pi-database', path: 'database' },
    ],
  },
  {
    title: 'SECURITY',
    items: [
      { label: 'Credentials', icon: 'pi-key', path: 'credentials' },
      { label: 'Mission Dashboard', icon: 'pi-shield', path: 'mission-dashboard' },
      { label: 'Fraud Detection', icon: 'pi-exclamation-triangle', path: 'fraud-detection' },
      { label: 'Content Policy', icon: 'pi-file-edit', path: 'content-policy' },
      { label: 'Rate Limits', icon: 'pi-gauge', path: 'rate-limits' },
    ],
  },
  {
    title: 'WEBMASTER',
    items: [
      { label: 'CMS / Content', icon: 'pi-file', path: 'cms' },
      { label: 'Localization', icon: 'pi-language', path: 'i18n' },
    ],
  },
  {
    title: 'COMMERCE',
    items: [
      { label: 'Orders', icon: 'pi-shopping-cart', path: 'orders' },
      { label: 'Pricing', icon: 'pi-dollar', path: 'pricing' },
      { label: 'Credits', icon: 'pi-wallet', path: 'credits' },
      { label: 'Payment Gateways', icon: 'pi-credit-card', path: 'payment-gateways' },
      { label: 'Shop', icon: 'pi-shop', path: 'shop' },
      { label: 'Shipping', icon: 'pi-truck', path: 'shipping' },
      { label: 'Manufacturing', icon: 'pi-cog', path: 'manufacturing' },
      { label: 'Promo Codes', icon: 'pi-tag', path: 'promo-codes' },
      { label: 'Inventory', icon: 'pi-box', path: 'inventory' },
      { label: 'Invoices', icon: 'pi-file', path: 'invoices' },
      { label: 'Tax Exemptions', icon: 'pi-percentage', path: 'tax-exemptions' },
      { label: 'Cart Recovery', icon: 'pi-undo', path: 'cart-abandonment' },
      { label: 'Customers', icon: 'pi-id-card', path: 'customers' },
      { label: 'CRM', icon: 'pi-chart-line', path: 'crm' },
    ],
  },
  {
    title: 'RANKING',
    items: [
      { label: 'Analytics', icon: 'pi-chart-bar', path: 'analytics' },
      { label: 'SEO Settings', icon: 'pi-search', path: 'seo' },
      { label: 'Domain Registry', icon: 'pi-globe', path: 'domains' },
      { label: 'Leads', icon: 'pi-users', path: 'leads' },
      { label: 'Lead Forms', icon: 'pi-list', path: 'lead-forms' },
      { label: 'Campaigns', icon: 'pi-send', path: 'campaigns' },
      { label: 'Lead Scoring', icon: 'pi-star', path: 'lead-scoring' },
    ],
  },
  {
    title: 'DEVELOPMENT',
    items: [
      { label: 'Dev Tickets', icon: 'pi-inbox', path: 'support' },
      { label: 'Repair Plans', icon: 'pi-wrench', path: 'repair-plans' },
      { label: 'Version Control', icon: 'pi-github', path: 'version-control' },
      { label: 'Dev Wiki', icon: 'pi-book', path: 'dev-wiki' },
    ],
  },
]

export function AdminLayout() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [avatarMenuOpen, setAvatarMenuOpen] = useState(false)
  const [drawerOpen, setDrawerOpen] = useState<'profile' | 'security' | 'preferences' | null>(null)
  const avatarMenuRef = useRef<HTMLDivElement>(null)
  const { user, logout } = useAdminAuth()
  const location = useLocation()

  const profile = user?.profile as Record<string, any> | undefined
  const givenName = profile?.given_name || ''
  const familyName = profile?.family_name || ''
  const userInitials = (givenName && familyName)
    ? `${givenName[0]}${familyName[0]}`.toUpperCase()
    : (profile?.email?.[0] || '?').toUpperCase()
  const userFullName = [givenName, familyName].filter(Boolean).join(' ') || profile?.email?.split('@')[0] || 'Admin'

  const currentPath = location.pathname.replace(/^\/admin\/?/, '').replace(/\/$/, '')

  // Breadcrumb generation
  const getBreadcrumbs = () => {
    if (!currentPath) return [{ label: 'Dashboard' }]
    for (const section of navigation) {
      for (const item of section.items) {
        if (item.path === currentPath) {
          const crumbs: { label: string; path?: string }[] = [{ label: 'Dashboard', path: '' }]
          if (section.title) crumbs.push({ label: section.title })
          crumbs.push({ label: item.label })
          return crumbs
        }
      }
    }
    // Handle parameterized routes
    if (currentPath.startsWith('tenants/') && currentPath !== 'tenants/onboard') {
      return [
        { label: 'Dashboard', path: '' },
        { label: 'PLATFORM' },
        { label: 'Tenants', path: 'tenants' },
        { label: 'Tenant Detail' },
      ]
    }
    return [{ label: 'Dashboard', path: '' }]
  }

  const breadcrumbs = getBreadcrumbs()

  return (
    <div className="flex min-h-screen">
      {/* Sidebar */}
      <aside
        className="fixed top-0 left-0 h-full bg-slate-800 text-slate-300 transition-all duration-300 z-40 flex flex-col"
        style={{ width: sidebarCollapsed ? '4rem' : '16.25rem' }}
      >
        {/* Logo */}
        <div className="flex items-center h-14 px-4 border-b border-slate-700">
          {!sidebarCollapsed && (
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center text-white font-bold text-sm">
                ID
              </div>
              <div>
                <div className="text-sm font-semibold text-white">IntelliDent AI</div>
                <div className="text-xs text-slate-400">Admin Console</div>
              </div>
            </div>
          )}
          {sidebarCollapsed && (
            <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center text-white font-bold text-sm mx-auto">
              ID
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="py-2 flex-1 overflow-y-auto">
          {navigation.map((section, sIdx) => (
            <div key={sIdx} className="mb-1">
              {section.title && !sidebarCollapsed && (
                <div className="px-4 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  {section.title}
                </div>
              )}
              {section.title && sidebarCollapsed && (
                <div className="border-t border-slate-700 my-1 mx-2" />
              )}
              {section.items.map((item) => {
                const isActive = currentPath === item.path
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    className={clsx(
                      'flex items-center gap-3 px-4 py-2 mx-2 rounded-lg text-sm transition-colors',
                      isActive
                        ? 'bg-blue-600 text-white'
                        : 'text-slate-300 hover:bg-slate-700 hover:text-white',
                      sidebarCollapsed && 'justify-center px-2',
                    )}
                    title={sidebarCollapsed ? item.label : undefined}
                    end={item.path === ''}
                  >
                    <i className={clsx('pi', item.icon, 'text-base')} />
                    {!sidebarCollapsed && (
                      <span className="flex-1">{item.label}</span>
                    )}
                    {!sidebarCollapsed && item.phase && (
                      <span className="text-xs px-1.5 py-0.5 rounded bg-slate-700 text-slate-400">
                        {item.phase}
                      </span>
                    )}
                  </NavLink>
                )
              })}
            </div>
          ))}
        </nav>

        {/* Theme Switcher — pinned to bottom */}
        <ThemeSwitcher collapsed={sidebarCollapsed} />
      </aside>

      {/* Main content area */}
      <div
        className="flex-1 transition-all duration-300"
        style={{ marginLeft: sidebarCollapsed ? '4rem' : '16.25rem' }}
      >
        {/* Topbar */}
        <header className="sticky top-0 z-30 h-14 bg-white border-b border-slate-200 flex items-center justify-between px-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="p-2 rounded-lg hover:bg-slate-100 text-slate-500"
            >
              <i className={clsx('pi', sidebarCollapsed ? 'pi-bars' : 'pi-times')} />
            </button>

            {/* Breadcrumbs */}
            <nav className="flex items-center gap-1 text-sm text-slate-500">
              {breadcrumbs.map((crumb, i) => (
                <span key={i} className="flex items-center gap-1">
                  {i > 0 && <i className="pi pi-chevron-right text-xs text-slate-300" />}
                  <span className={i === breadcrumbs.length - 1 ? 'text-slate-800 font-medium' : ''}>
                    {crumb.label}
                  </span>
                </span>
              ))}
            </nav>
          </div>

          <div className="flex items-center gap-3 relative">
            <button
              onClick={() => setAvatarMenuOpen(!avatarMenuOpen)}
              className="w-9 h-9 bg-blue-600 rounded-full flex items-center justify-center text-white text-sm font-bold hover:bg-blue-700 transition-colors"
              title={userFullName}
            >
              {userInitials}
            </button>

            {/* Avatar dropdown */}
            {avatarMenuOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setAvatarMenuOpen(false)} />
                <div ref={avatarMenuRef} className="absolute right-0 top-12 z-50 w-72 bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden">
                  <div className="p-4 border-b border-slate-100">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold shrink-0">
                        {userInitials}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-slate-800 truncate">{userFullName}</p>
                        <p className="text-xs text-slate-400 truncate">{user?.profile?.email}</p>
                        <span className="inline-block mt-0.5 text-[9px] px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded-full font-medium">
                          platform-operator
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="py-1">
                    <button onClick={() => { setAvatarMenuOpen(false); setDrawerOpen('profile') }}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-600 hover:bg-slate-50 transition-colors">
                      <i className="pi pi-user text-slate-400" /> My Profile
                    </button>
                    <button onClick={() => { setAvatarMenuOpen(false); setDrawerOpen('security') }}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-600 hover:bg-slate-50 transition-colors">
                      <i className="pi pi-lock text-slate-400" /> Change Password
                    </button>
                  </div>
                  <div className="border-t border-slate-100 py-1">
                    <button onClick={() => { setAvatarMenuOpen(false); logout() }}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors">
                      <i className="pi pi-sign-out text-red-400" /> Sign Out
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </header>

        {/* Page content */}
        <main className="p-6">
          <Outlet />
        </main>
        <ToastHost />
      </div>
      {/* Account Drawer */}
      <AccountDrawer
        open={drawerOpen !== null}
        initialTab={drawerOpen || 'profile'}
        onClose={() => setDrawerOpen(null)}
      />
    </div>
  )
}
